import { useCallback, useEffect, useRef, useState } from 'react';
import { Lesson } from '../../api/types/lesson';
import { getLessonById, updateLesson } from '../../api/lesson';
import { normalizeLrc } from '../../utils/transcript/lrc';
import { isProjectError } from '../../utils/errors';
import { TranscriberStage } from '../../hooks/useTranscriber';

export type BatchRowStatus = 'queued' | 'downloading' | 'transcribing' | 'saved' | 'failed' | 'cancelled';

export interface BatchRowState {
  status: BatchRowStatus;
  message?: string;
  seconds?: number;
  /** `true` once the active row's audio was served from a prefetch cache rather than downloaded live. */
  audioReady?: boolean;
}

export type BatchRunState = 'idle' | 'running' | 'stopping';

interface UseBatchTranscriptionArgs {
  transcribe: (audioPath: string) => Promise<{
    lrc: string;
    timingsMs: { total: number };
    downloadMode: 'parallel' | 'single' | 'prefetched';
  }>;
  prefetch: (audioPath: string) => void;
  cancel: () => void;
  stage: TranscriberStage;
  onLessonSaved: (lessonId: string, lrc: string, durationMs?: number) => void;
}

/**
 * Owns the sequential batch-transcription loop: processes one lesson at a
 * time, prefetches the next lesson's audio as soon as the current one starts,
 * saves each result immediately, and tracks per-row status for the UI.
 */
export const useBatchTranscription = ({
  transcribe,
  prefetch,
  cancel,
  stage,
  onLessonSaved,
}: UseBatchTranscriptionArgs) => {
  const [runState, setRunState] = useState<BatchRunState>('idle');
  const [rowStates, setRowStates] = useState<Record<string, BatchRowState>>({});
  const [activeLessonId, setActiveLessonId] = useState<string | undefined>();
  const [prefetchLessonId, setPrefetchLessonId] = useState<string | undefined>();

  // Guards against a cancelled/stale run continuing to process lessons after
  // `cancel()`/unmount, since the loop below is a plain async function.
  const runIdRef = useRef(0);
  const stopRequestedRef = useRef(false);
  // Lesson ids the loop already called `prefetch()` for, so the next iteration
  // knows to show "Audio ready" instead of a download phase for that row.
  const prefetchedLessonIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      runIdRef.current += 1;
    };
  }, []);

  const setRowState = useCallback((lessonId: string, state: BatchRowState) => {
    setRowStates((current) => ({ ...current, [lessonId]: state }));
  }, []);

  const run = useCallback(
    async (lessons: Lesson[]) => {
      const runId = ++runIdRef.current;
      stopRequestedRef.current = false;
      setRunState('running');

      const initialStates: Record<string, BatchRowState> = {};
      lessons.forEach((lesson) => {
        initialStates[lesson.id] = { status: 'queued' };
      });
      setRowStates((current) => ({ ...current, ...initialStates }));

      const queue = lessons;

      for (let index = 0; index < queue.length; index++) {
        if (runIdRef.current !== runId) {
          return;
        }
        if (stopRequestedRef.current) {
          break;
        }

        const lesson = queue[index];
        const audioPath = lesson.audioStreamPath;
        if (!audioPath) {
          setRowState(lesson.id, { status: 'failed', message: 'Lesson has no audio file.' });
          continue;
        }

        setActiveLessonId(lesson.id);
        setPrefetchLessonId((current) => (current === lesson.id ? undefined : current));
        const wasPrefetched = prefetchedLessonIdsRef.current.has(lesson.id);
        prefetchedLessonIdsRef.current.delete(lesson.id);
        setRowState(lesson.id, { status: 'downloading', audioReady: wasPrefetched });

        const next = queue[index + 1];
        if (next?.audioStreamPath) {
          setPrefetchLessonId(next.id);
          prefetchedLessonIdsRef.current.add(next.id);
          prefetch(next.audioStreamPath);
        }

        const startedAt = performance.now();
        try {
          const result = await transcribe(audioPath);
          if (runIdRef.current !== runId) {
            return;
          }

          const normalized = normalizeLrc(result.lrc);

          // Safety check: skip saving if the lesson already got a transcript
          // elsewhere (e.g. another tab) while this one was transcribing.
          const latestLesson = await getLessonById(lesson.id);
          if (runIdRef.current !== runId) {
            return;
          }
          if (latestLesson?.lrcTranscript?.trim()?.length) {
            setRowState(lesson.id, { status: 'cancelled', message: 'Lesson already has a transcript.' });
            continue;
          }

          const saveResult = await updateLesson({ id: lesson.id, lrcTranscript: normalized });
          if (runIdRef.current !== runId) {
            return;
          }

          if (isProjectError(saveResult)) {
            setRowState(lesson.id, { status: 'failed', message: 'Failed to save the transcript.' });
          } else {
            const durationMs = result.timingsMs?.total ?? performance.now() - startedAt;
            onLessonSaved(lesson.id, normalized, durationMs);
            setRowState(lesson.id, {
              status: 'saved',
              seconds: Math.round(durationMs / 1000),
            });
          }
        } catch (err) {
          if (runIdRef.current !== runId) {
            return;
          }
          const message = err instanceof Error ? err.message : String(err);
          if (message === 'Transcription cancelled') {
            setRowState(lesson.id, { status: 'cancelled', message: 'Cancelled.' });
          } else {
            setRowState(lesson.id, { status: 'failed', message });
          }
        }
      }

      if (runIdRef.current === runId) {
        setActiveLessonId(undefined);
        setRunState('idle');
        stopRequestedRef.current = false;
      }
    },
    [transcribe, prefetch, onLessonSaved, setRowState],
  );

  const stopAfterCurrent = useCallback(() => {
    stopRequestedRef.current = true;
    setRunState('stopping');
  }, []);

  const cancelNow = useCallback(() => {
    runIdRef.current += 1;
    stopRequestedRef.current = false;
    if (activeLessonId) {
      setRowState(activeLessonId, { status: 'cancelled', message: 'Cancelled.' });
    }
    cancel();
    prefetchedLessonIdsRef.current.clear();
    setActiveLessonId(undefined);
    setPrefetchLessonId(undefined);
    setRunState('idle');
  }, [activeLessonId, cancel, setRowState]);

  const resetRowStates = useCallback((lessonIds?: string[]) => {
    if (!lessonIds) {
      setRowStates({});
      return;
    }
    setRowStates((current) => {
      const next = { ...current };
      lessonIds.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  const activeRowStage = runState === 'running' ? stage : undefined;

  return {
    runState,
    rowStates,
    activeLessonId,
    prefetchLessonId,
    activeRowStage,
    run,
    stopAfterCurrent,
    cancelNow,
    resetRowStates,
  };
};

export default useBatchTranscription;
